import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import api from '../../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Reports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('weekly');
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childReport, setChildReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  // 현재 주/월 기본값 설정
  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - dayOfWeek);

    setSelectedWeek(weekStart.toISOString().split('T')[0]);
    setSelectedMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  }, []);

  // 주간 리포트 로드
  const loadWeeklyReport = async (week = selectedWeek) => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/weekly${week ? `?week=${week}` : ''}`);
      setWeeklyData(response.data);
    } catch (error) {
      console.error('주간 리포트 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 월간 리포트 로드
  const loadMonthlyReport = async (month = selectedMonth) => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/monthly${month ? `?month=${month}` : ''}`);
      setMonthlyData(response.data);
    } catch (error) {
      console.error('월간 리포트 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 자녀별 상세 리포트 로드
  const loadChildReport = async (childId) => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/child/${childId}?weeks=4`);
      setChildReport(response.data);
    } catch (error) {
      console.error('자녀 리포트 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'weekly' && selectedWeek) {
      loadWeeklyReport(selectedWeek);
    } else if (activeTab === 'monthly' && selectedMonth) {
      loadMonthlyReport(selectedMonth);
    }
  }, [activeTab, selectedWeek, selectedMonth]);

  const handleChildSelect = (child) => {
    setSelectedChild(child);
    loadChildReport(child.id);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const TabButton = ({ id, label, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`tab-button ${active ? 'active' : ''}`}
    >
      {label}
    </button>
  );

  return (
    <div className="reports-container">
      <div className="reports-header">
        <div className="header-section">
          <button
            className="btn btn-outline back-btn"
            onClick={() => navigate('/parent/dashboard')}
          >
            ← 뒤로가기
          </button>
        </div>
        <h1 className="reports-title">📊 가족 리포트</h1>
        <p className="reports-subtitle">미션 수행 현황과 용돈 관리 통계를 확인하세요</p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="tab-navigation">
        <div className="tab-buttons">
          <TabButton
            id="weekly"
            label="주간 리포트"
            active={activeTab === 'weekly'}
            onClick={setActiveTab}
          />
          <TabButton
            id="monthly"
            label="월간 리포트"
            active={activeTab === 'monthly'}
            onClick={setActiveTab}
          />
          <TabButton
            id="child"
            label="자녀별 상세"
            active={activeTab === 'child'}
            onClick={setActiveTab}
          />
        </div>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-text">데이터를 불러오는 중...</div>
        </div>
      )}

      {/* 주간 리포트 */}
      {activeTab === 'weekly' && weeklyData && !loading && (
        <div className="report-content">
          {/* 주간 선택 */}
          <div className="date-selector">
            <label className="selector-label">주간 선택</label>
            <input
              type="date"
              value={selectedWeek}
              onChange={(e) => {
                setSelectedWeek(e.target.value);
                loadWeeklyReport(e.target.value);
              }}
              className="date-input"
            />
            <span className="date-period">
              ({weeklyData.period.startDate} ~ {weeklyData.period.endDate})
            </span>
          </div>

          {/* 요약 카드 */}
          <div className="summary-grid">
            <div className="summary-card">
              <div className="card-number blue">{weeklyData.summary.totalChildren}</div>
              <div className="card-label">자녀 수</div>
            </div>
            <div className="summary-card">
              <div className="card-number green">{weeklyData.summary.averageCompletionRate}%</div>
              <div className="card-label">평균 완료율</div>
            </div>
            <div className="summary-card">
              <div className="card-number purple">{formatCurrency(weeklyData.summary.totalSettlementAmount)}</div>
              <div className="card-label">총 정산금액</div>
            </div>
            <div className="summary-card">
              <div className="card-number orange">{formatCurrency(weeklyData.summary.totalSpent)}</div>
              <div className="card-label">총 사용금액</div>
            </div>
          </div>

          {/* 자녀별 상세 */}
          {weeklyData.children.map((child) => (
            <div key={child.child.id} className="child-report-card">
              <div className="card-header">
                <h3>{child.child.name}의 주간 현황</h3>
              </div>
              <div className="card-content">
                <div className="details-grid">
                  <div className="detail-section">
                    <h4>미션 수행</h4>
                    <div className="detail-item">
                      <span>전체 미션:</span>
                      <span className="value">{child.missions.total}개</span>
                    </div>
                    <div className="detail-item">
                      <span>완료한 미션:</span>
                      <span className="value green">{child.missions.completed}개</span>
                    </div>
                    <div className="detail-item">
                      <span>완료율:</span>
                      <span className="value blue">{child.missions.completionRate}%</span>
                    </div>
                    <div className="detail-item">
                      <span>가중 완료율:</span>
                      <span className="value purple">{child.missions.weightedCompletionRate}%</span>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>지갑 현황</h4>
                    <div className="detail-item">
                      <span>지급받은 도토리:</span>
                      <span className="value green">+{formatCurrency(child.wallet.earned)}</span>
                    </div>
                    <div className="detail-item">
                      <span>사용한 도토리:</span>
                      <span className="value red">-{formatCurrency(child.wallet.spent)}</span>
                    </div>
                    <div className="detail-item">
                      <span>보너스:</span>
                      <span className="value blue">+{formatCurrency(child.wallet.bonus)}</span>
                    </div>
                    <div className="detail-item">
                      <span>벌점:</span>
                      <span className="value red">-{formatCurrency(child.wallet.penalty)}</span>
                    </div>
                  </div>
                </div>
                {child.settlement && (
                  <div className="settlement-info">
                    <div className="settlement-amount">
                      ✓ 정산 완료: {formatCurrency(child.settlement.finalAmount)} 도토리
                    </div>
                    <div className="settlement-date">
                      {new Date(child.settlement.confirmedAt).toLocaleDateString('ko-KR')} 지급
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 월간 리포트 */}
      {activeTab === 'monthly' && monthlyData && !loading && (
        <div className="report-content">
          {/* 월간 선택 */}
          <div className="date-selector">
            <label className="selector-label">월간 선택</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                loadMonthlyReport(e.target.value);
              }}
              className="date-input"
            />
            <span className="date-period">
              ({monthlyData.period.startDate} ~ {monthlyData.period.endDate})
            </span>
          </div>

          {/* 월간 요약 */}
          <div className="summary-grid monthly">
            <div className="summary-card">
              <div className="card-number blue">{monthlyData.summary.averageCompletionRate}%</div>
              <div className="card-label">평균 완료율</div>
            </div>
            <div className="summary-card">
              <div className="card-number green">{monthlyData.summary.totalSettlements}</div>
              <div className="card-label">총 정산 횟수</div>
            </div>
            <div className="summary-card">
              <div className="card-number purple">{formatCurrency(monthlyData.summary.totalSettlementAmount)}</div>
              <div className="card-label">총 정산금액</div>
            </div>
            <div className="summary-card">
              <div className="card-number orange">{formatCurrency(monthlyData.summary.totalSpent)}</div>
              <div className="card-label">총 사용금액</div>
            </div>
            <div className="summary-card">
              <div className="card-number indigo">{formatCurrency(monthlyData.summary.totalCurrentBalance)}</div>
              <div className="card-label">현재 총 잔액</div>
            </div>
          </div>

          {/* 월간 전체 차트 */}
          {monthlyData.children.length > 0 && (
            <div className="chart-card">
              <div className="chart-header">
                <h3>월간 자녀별 완료율 비교</h3>
              </div>
              <div className="chart-content">
                <Bar
                  data={{
                    labels: monthlyData.children.map(child => child.child.name),
                    datasets: [
                      {
                        label: '완료율 (%)',
                        data: monthlyData.children.map(child => child.missions.completionRate),
                        backgroundColor: 'rgba(34, 197, 94, 0.8)',
                        borderColor: 'rgba(34, 197, 94, 1)',
                        borderWidth: 1
                      },
                      {
                        label: '필수 미션 완료율 (%)',
                        data: monthlyData.children.map(child =>
                          child.missions.totalRequired > 0
                            ? Math.round((child.missions.requiredCompleted / child.missions.totalRequired) * 100)
                            : 0
                        ),
                        backgroundColor: 'rgba(168, 85, 247, 0.8)',
                        borderColor: 'rgba(168, 85, 247, 1)',
                        borderWidth: 1
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          callback: function(value) {
                            return value + '%';
                          }
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'top'
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return context.dataset.label + ': ' + context.parsed.y + '%';
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* 월간 정산 금액 트렌드 */}
          {monthlyData.children.length > 0 && monthlyData.children.some(child => child.settlements.details.length > 0) && (
            <div className="chart-card">
              <div className="chart-header">
                <h3>월간 주별 정산 금액 트렌드</h3>
              </div>
              <div className="chart-content">
                <Line
                  data={{
                    labels: monthlyData.children[0].settlements.details.map(settlement =>
                      new Date(settlement.week).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
                    ),
                    datasets: monthlyData.children.map((child, index) => ({
                      label: child.child.name,
                      data: child.settlements.details.map(settlement => settlement.amount),
                      borderColor: [
                        'rgba(34, 197, 94, 1)',
                        'rgba(59, 130, 246, 1)',
                        'rgba(168, 85, 247, 1)',
                        'rgba(245, 158, 11, 1)',
                        'rgba(239, 68, 68, 1)'
                      ][index % 5],
                      backgroundColor: [
                        'rgba(34, 197, 94, 0.1)',
                        'rgba(59, 130, 246, 0.1)',
                        'rgba(168, 85, 247, 0.1)',
                        'rgba(245, 158, 11, 0.1)',
                        'rgba(239, 68, 68, 0.1)'
                      ][index % 5],
                      borderWidth: 2,
                      fill: false,
                      tension: 0.1
                    }))
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: function(value) {
                            return formatCurrency(value) + '원';
                          }
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'top'
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return context.dataset.label + ': ' + formatCurrency(context.parsed.y) + '원';
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* 지갑 현황 도넛 차트 */}
          {monthlyData.children.length > 0 && (
            <div className="chart-card">
              <div className="chart-header">
                <h3>월간 자녀별 잔액 분포</h3>
              </div>
              <div className="chart-content">
                <Doughnut
                  data={{
                    labels: monthlyData.children.map(child => child.child.name),
                    datasets: [{
                      data: monthlyData.children.map(child => child.wallet.currentBalance),
                      backgroundColor: [
                        'rgba(34, 197, 94, 0.8)',
                        'rgba(59, 130, 246, 0.8)',
                        'rgba(168, 85, 247, 0.8)',
                        'rgba(245, 158, 11, 0.8)',
                        'rgba(239, 68, 68, 0.8)'
                      ],
                      borderColor: [
                        'rgba(34, 197, 94, 1)',
                        'rgba(59, 130, 246, 1)',
                        'rgba(168, 85, 247, 1)',
                        'rgba(245, 158, 11, 1)',
                        'rgba(239, 68, 68, 1)'
                      ],
                      borderWidth: 2
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right'
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const total = context.dataset.data.reduce((sum, value) => sum + value, 0);
                            const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                            return context.label + ': ' + formatCurrency(context.parsed) + '원 (' + percentage + '%)';
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* 자녀별 월간 현황 */}
          {monthlyData.children.map((child) => (
            <div key={child.child.id} className="child-report-card">
              <div className="card-header">
                <h3>{child.child.name}의 월간 현황</h3>
              </div>
              <div className="card-content">
                <div className="details-grid three-cols">
                  <div className="detail-section">
                    <h4>미션 현황</h4>
                    <div className="detail-item">
                      <span>전체 미션:</span>
                      <span className="value">{child.missions.total}개</span>
                    </div>
                    <div className="detail-item">
                      <span>완료율:</span>
                      <span className="value green">{child.missions.completionRate}%</span>
                    </div>
                    <div className="detail-item">
                      <span>필수 미션:</span>
                      <span className="value">{child.missions.requiredCompleted}/{child.missions.totalRequired}</span>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>정산 현황</h4>
                    <div className="detail-item">
                      <span>정산 횟수:</span>
                      <span className="value">{child.settlements.count}회</span>
                    </div>
                    <div className="detail-item">
                      <span>총 정산금액:</span>
                      <span className="value green">{formatCurrency(child.settlements.totalAmount)}</span>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>지갑 현황</h4>
                    <div className="detail-item">
                      <span>현재 잔액:</span>
                      <span className="value blue">{formatCurrency(child.wallet.currentBalance)}</span>
                    </div>
                    <div className="detail-item">
                      <span>순 증감:</span>
                      <span className={`value ${child.wallet.netAmount >= 0 ? 'green' : 'red'}`}>
                        {child.wallet.netAmount >= 0 ? '+' : ''}
                        {formatCurrency(child.wallet.netAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 개별 자녀 주별 정산 내역 */}
                {child.settlements.details.length > 0 && (
                  <div className="settlement-details">
                    <h4>주별 정산 내역</h4>
                    <div className="settlement-grid">
                      {child.settlements.details.map((settlement, index) => (
                        <div key={index} className="settlement-item">
                          <div className="settlement-date">
                            {new Date(settlement.week).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                          </div>
                          <div className="settlement-amount green">
                            {formatCurrency(settlement.amount)}
                          </div>
                          <div className="settlement-rate">
                            {settlement.completionRate}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 자녀별 상세 리포트 */}
      {activeTab === 'child' && (
        <div className="report-content">
          {/* 자녀 선택 */}
          {!selectedChild && weeklyData && (
            <div className="child-select-card">
              <div className="card-header">
                <h3>자녀 선택</h3>
              </div>
              <div className="card-content">
                <div className="child-select-grid">
                  {weeklyData.children.map((child) => (
                    <button
                      key={child.child.id}
                      onClick={() => handleChildSelect(child.child)}
                      className="child-select-button"
                    >
                      <div className="child-name">{child.child.name}</div>
                      <div className="child-description">상세 리포트 보기</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 선택된 자녀의 상세 리포트 */}
          {selectedChild && childReport && !loading && (
            <div className="child-detail-report">
              <div className="detail-header">
                <h2>{selectedChild.name}의 상세 리포트</h2>
                <button
                  onClick={() => {
                    setSelectedChild(null);
                    setChildReport(null);
                  }}
                  className="btn btn-outline"
                >
                  ← 뒤로가기
                </button>
              </div>

              {/* 전체 통계 */}
              <div className="summary-grid">
                <div className="summary-card">
                  <div className="card-number blue">{childReport.stats.totalSettlements}</div>
                  <div className="card-label">총 정산 횟수</div>
                </div>
                <div className="summary-card">
                  <div className="card-number green">{childReport.stats.averageCompletionRate}%</div>
                  <div className="card-label">평균 완료율</div>
                </div>
                <div className="summary-card">
                  <div className="card-number purple">{formatCurrency(childReport.stats.totalEarned)}</div>
                  <div className="card-label">총 획득 도토리</div>
                </div>
                <div className="summary-card">
                  <div className="card-number indigo">{formatCurrency(childReport.stats.currentBalance)}</div>
                  <div className="card-label">현재 잔액</div>
                </div>
              </div>

              {/* 주간 추이 */}
              {childReport.weeklyTrend.length > 0 && (
                <div className="weekly-trend-card">
                  <div className="card-header">
                    <h3>주간 완료율 추이</h3>
                  </div>
                  <div className="card-content">
                    <div className="trend-list">
                      {childReport.weeklyTrend.map((week, index) => (
                        <div key={index} className="trend-item">
                          <div className="trend-info">
                            <div className="trend-week">
                              {new Date(week.week).toLocaleDateString('ko-KR')} 주간
                            </div>
                            <div className="trend-missions">
                              {week.completedMissions}/{week.totalMissions} 미션 완료
                            </div>
                          </div>
                          <div className="trend-results">
                            <div className="trend-rate green">
                              {week.completionRate}%
                            </div>
                            <div className="trend-amount purple">
                              {formatCurrency(week.finalAmount)} 도토리
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 최근 거래 내역 */}
              <div className="transactions-card">
                <div className="card-header">
                  <h3>최근 거래 내역</h3>
                </div>
                <div className="card-content">
                  <div className="transaction-list">
                    {childReport.recentTransactions.map((tx) => (
                      <div key={tx.id} className="transaction-item">
                        <div className="transaction-info">
                          <div className="transaction-desc">{tx.description}</div>
                          <div className="transaction-date">
                            {new Date(tx.createdAt).toLocaleDateString('ko-KR')}
                          </div>
                        </div>
                        <div className="transaction-amount">
                          <div className={`amount ${
                            tx.type === 'earn' || tx.type === 'bonus' ? 'green' : 'red'
                          }`}>
                            {tx.type === 'earn' || tx.type === 'bonus' ? '+' : '-'}
                            {formatCurrency(tx.amount)}
                          </div>
                          <div className="balance">
                            잔액: {formatCurrency(tx.balanceAfter)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .reports-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 20px;
        }

        .reports-header {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          margin-bottom: 30px;
        }

        .header-section {
          margin-bottom: 20px;
        }

        .back-btn {
          margin-bottom: 0;
        }

        .reports-title {
          margin: 0 0 10px 0;
          color: #333;
          font-size: 2rem;
          font-weight: bold;
        }

        .reports-subtitle {
          color: #666;
          font-size: 1.1rem;
          margin: 0;
        }

        .tab-navigation {
          background: white;
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          margin-bottom: 30px;
        }

        .tab-buttons {
          display: flex;
          gap: 5px;
        }

        .tab-button {
          padding: 12px 24px;
          font-weight: 500;
          border-radius: 8px 8px 0 0;
          transition: all 0.2s;
          background: #f3f4f6;
          color: #374151;
          border: none;
          cursor: pointer;
        }

        .tab-button:hover {
          background: #e5e7eb;
        }

        .tab-button.active {
          background: #10b981;
          color: white;
        }

        .loading-container {
          display: flex;
          justify-content: center;
          padding: 50px 0;
        }

        .loading-text {
          color: #6b7280;
          font-size: 1.1rem;
        }

        .report-content {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        .date-selector {
          background: white;
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .selector-label {
          font-weight: 500;
          color: #374151;
          font-size: 0.9rem;
        }

        .date-input {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .date-input:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .date-period {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }

        .summary-grid.monthly {
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        }

        .summary-card {
          background: white;
          padding: 25px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          text-align: center;
          transition: transform 0.2s;
        }

        .summary-card:hover {
          transform: translateY(-3px);
        }

        .card-number {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 8px;
        }

        .card-number.blue { color: #3b82f6; }
        .card-number.green { color: #10b981; }
        .card-number.purple { color: #8b5cf6; }
        .card-number.orange { color: #f59e0b; }
        .card-number.indigo { color: #6366f1; }

        .card-label {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .child-report-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .card-header {
          background: #f9fafb;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .card-header h3 {
          margin: 0;
          color: #374151;
          font-size: 1.2rem;
        }

        .card-content {
          padding: 25px;
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 30px;
        }

        .details-grid.three-cols {
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        }

        .detail-section h4 {
          color: #374151;
          font-weight: 500;
          margin-bottom: 15px;
          font-size: 1rem;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 0.9rem;
        }

        .detail-item span:first-child {
          color: #6b7280;
        }

        .value {
          font-weight: 500;
        }

        .value.green { color: #10b981; }
        .value.red { color: #ef4444; }
        .value.blue { color: #3b82f6; }
        .value.purple { color: #8b5cf6; }

        .settlement-info {
          margin-top: 20px;
          padding: 15px;
          background: #ecfdf5;
          border-radius: 8px;
        }

        .settlement-amount {
          font-weight: 500;
          color: #059669;
          margin-bottom: 5px;
        }

        .settlement-date {
          font-size: 0.9rem;
          color: #065f46;
        }

        .settlement-details {
          margin-top: 25px;
        }

        .settlement-details h4 {
          color: #374151;
          font-weight: 500;
          margin-bottom: 15px;
        }

        .settlement-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 12px;
        }

        .settlement-item {
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          text-align: center;
        }

        .settlement-date {
          font-size: 0.8rem;
          color: #6b7280;
          margin-bottom: 5px;
        }

        .settlement-amount {
          font-weight: 500;
          margin-bottom: 3px;
        }

        .settlement-rate {
          font-size: 0.8rem;
          color: #6b7280;
        }

        .chart-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .chart-header {
          background: #f9fafb;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .chart-header h3 {
          margin: 0;
          color: #374151;
          font-size: 1.2rem;
        }

        .chart-content {
          padding: 25px;
          height: 320px;
        }

        .child-select-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .child-select-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }

        .child-select-button {
          padding: 20px;
          text-align: left;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .child-select-button:hover {
          border-color: #10b981;
          background: #ecfdf5;
        }

        .child-name {
          font-weight: 500;
          color: #374151;
          margin-bottom: 5px;
        }

        .child-description {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .child-detail-report {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        .detail-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .detail-header h2 {
          margin: 0;
          color: #374151;
          font-size: 1.5rem;
        }

        .weekly-trend-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .trend-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .trend-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .trend-week {
          font-weight: 500;
          color: #374151;
          margin-bottom: 3px;
        }

        .trend-missions {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .trend-results {
          text-align: right;
        }

        .trend-rate {
          font-weight: 500;
          margin-bottom: 3px;
        }

        .trend-amount {
          font-size: 0.9rem;
        }

        .transactions-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .transaction-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .transaction-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          border-bottom: 1px solid #f3f4f6;
        }

        .transaction-item:last-child {
          border-bottom: none;
        }

        .transaction-desc {
          font-weight: 500;
          color: #374151;
          margin-bottom: 3px;
        }

        .transaction-date {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .transaction-amount {
          text-align: right;
        }

        .amount {
          font-weight: 500;
          margin-bottom: 3px;
        }

        .balance {
          font-size: 0.9rem;
          color: #6b7280;
        }

        @media (max-width: 768px) {
          .reports-container {
            padding: 15px;
          }

          .summary-grid {
            grid-template-columns: 1fr;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }

          .tab-buttons {
            flex-direction: column;
            gap: 8px;
          }

          .tab-button {
            border-radius: 8px;
          }

          .date-selector {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .detail-header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }

          .trend-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .trend-results {
            text-align: left;
            width: 100%;
          }

          .transaction-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .transaction-amount {
            text-align: left;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Reports;